/**
 * 後台 — 溯源數位憑證管理(#11)
 *
 * 列表所有溯源燕屋 + 新增/編輯完整履歷:
 *   基本(批號範圍 / 燕屋編號 / 標題)、產地+GPS、生態建築、官方認證、工藝圖庫、品牌敘述。
 * batch_range from/to 是查詢關鍵(user 輸批號 → 落在範圍內的燕屋)。
 */
import { useEffect, useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import {
  adminListTraceability,
  adminUpsertTraceability,
  adminDeleteTraceability,
  adminUploadTraceabilityImage,
  type TraceabilityDoc,
} from "../../api/admin";
import AdminHeader from "../../components/AdminHeader";
import { useAuthStore } from "../../store/authStore";
import { useUIStore } from "../../store/uiStore";

type GalleryItem = { url: string; caption: string };

type FormState = {
  doc_id: string;
  batchFrom: string;
  batchTo: string;
  house_id: string;
  headline_zh: string;
  headline_en: string;
  country_zh: string;
  state_zh: string;
  district_zh: string;
  district: string;
  gpsLat: string;
  gpsLng: string;
  bldDesc: string;
  bldFloors: string;
  bldArea: string;
  bldFounded: string;
  bldHarvest: string;
  certAuthorityZh: string;
  certAuthorityFull: string;
  certReviewer: string;
  certDate: string;
  hero: string;
  gallery: GalleryItem[];
  narrative: string[];
  source_url: string;
};

const EMPTY_FORM: FormState = {
  doc_id: "", batchFrom: "", batchTo: "", house_id: "",
  headline_zh: "", headline_en: "",
  country_zh: "", state_zh: "", district_zh: "", district: "", gpsLat: "", gpsLng: "",
  bldDesc: "", bldFloors: "", bldArea: "", bldFounded: "", bldHarvest: "",
  certAuthorityZh: "", certAuthorityFull: "", certReviewer: "", certDate: "",
  hero: "", gallery: [], narrative: [], source_url: "",
};

function docToForm(d: TraceabilityDoc): FormState {
  const gallery: GalleryItem[] = (d.images?.gallery || []).map((url, i) => ({
    url,
    caption: d.captions_zh?.[i] || "",
  }));
  return {
    doc_id: d.doc_id,
    batchFrom: String(d.batch_range?.from ?? ""),
    batchTo: String(d.batch_range?.to ?? ""),
    house_id: d.house_id || "",
    headline_zh: d.headline_zh || "",
    headline_en: d.headline_en || "",
    country_zh: d.location?.country_zh || "",
    state_zh: d.location?.state_zh || "",
    district_zh: d.location?.district_zh || "",
    district: d.location?.district || "",
    gpsLat: String(d.location?.gps?.lat ?? ""),
    gpsLng: String(d.location?.gps?.lng ?? ""),
    bldDesc: d.building?.description_zh || "",
    bldFloors: String(d.building?.floors ?? ""),
    bldArea: String(d.building?.area_sqft ?? ""),
    bldFounded: String(d.building?.founded_year ?? ""),
    bldHarvest: String(d.building?.harvesting_years ?? ""),
    certAuthorityZh: d.certification?.authority_zh || "",
    certAuthorityFull: d.certification?.authority_full || "",
    certReviewer: d.certification?.reviewer_zh || "",
    certDate: d.certification?.date || "",
    hero: d.images?.hero || "",
    gallery,
    narrative: d.narrative_zh || [],
    source_url: d.source_url || "",
  };
}

export default function TraceabilityManage() {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const showToast = useUIStore((s) => s.showToast);

  const [rows, setRows] = useState<TraceabilityDoc[]>([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [uploadingKey, setUploadingKey] = useState(""); // "hero" / `g${i}` / "new" — 正在上傳哪張

  useEffect(() => {
    if (role !== "admin") {
      navigate("/admin/login", { replace: true });
      return;
    }
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role]);

  async function refresh() {
    setLoading(true);
    try {
      setRows(await adminListTraceability());
    } catch {
      showToast("載入失敗", "error");
    } finally {
      setLoading(false);
    }
  }

  function startNew() {
    setForm(EMPTY_FORM);
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function startEdit(d: TraceabilityDoc) {
    setForm(docToForm(d));
    setEditing(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
  function cancel() {
    setForm(EMPTY_FORM);
    setEditing(false);
  }
  function set<K extends keyof FormState>(k: K, v: FormState[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  // 選好檔案 → 立刻上傳到我們圖庫 → 回 url(失敗回 null,前端不留空 row)
  async function uploadImage(file: File | undefined, key: string): Promise<string | null> {
    if (!file) return null;
    setUploadingKey(key);
    try {
      const r = await adminUploadTraceabilityImage(file);
      if (r.status === "ok" && r.url) return r.url;
      showToast(`上傳失敗:${r.status}`, "error");
      return null;
    } catch {
      showToast("上傳失敗(檔案太大或格式不符)", "error");
      return null;
    } finally {
      setUploadingKey("");
    }
  }
  async function pickHero(file?: File) {
    const url = await uploadImage(file, "hero");
    if (url) set("hero", url);
  }
  async function replaceGallery(i: number, file?: File) {
    const url = await uploadImage(file, `g${i}`);
    if (url) updateGallery(i, "url", url);
  }
  async function addGalleryImage(file?: File) {
    const url = await uploadImage(file, "new");
    if (url) setForm((f) => ({ ...f, gallery: [...f.gallery, { url, caption: "" }] }));
  }
  function updateGallery(i: number, field: "url" | "caption", v: string) {
    setForm((f) => ({ ...f, gallery: f.gallery.map((g, idx) => (idx === i ? { ...g, [field]: v } : g)) }));
  }
  function removeGallery(i: number) {
    setForm((f) => ({ ...f, gallery: f.gallery.filter((_, idx) => idx !== i) }));
  }
  function addNarrative() {
    setForm((f) => ({ ...f, narrative: [...f.narrative, ""] }));
  }
  function updateNarrative(i: number, v: string) {
    setForm((f) => ({ ...f, narrative: f.narrative.map((p, idx) => (idx === i ? v : p)) }));
  }
  function removeNarrative(i: number) {
    setForm((f) => ({ ...f, narrative: f.narrative.filter((_, idx) => idx !== i) }));
  }

  async function save() {
    if (!form.batchFrom.trim() || !form.batchTo.trim()) {
      showToast("請填批號範圍(起 / 迄)", "error");
      return;
    }
    setSubmitting(true);
    try {
      const gallery = form.gallery.filter((g) => g.url.trim());
      const r = await adminUpsertTraceability({
        doc_id: form.doc_id || undefined,
        batch_range: { from: parseInt(form.batchFrom, 10) || 0, to: parseInt(form.batchTo, 10) || 0 },
        house_id: form.house_id.trim(),
        headline_zh: form.headline_zh.trim(),
        headline_en: form.headline_en.trim(),
        location: {
          country_zh: form.country_zh.trim(),
          state_zh: form.state_zh.trim(),
          district_zh: form.district_zh.trim(),
          district: form.district.trim(),
          gps: { lat: parseFloat(form.gpsLat) || 0, lng: parseFloat(form.gpsLng) || 0 },
        },
        building: {
          description_zh: form.bldDesc.trim(),
          floors: parseInt(form.bldFloors, 10) || 0,
          area_sqft: parseInt(form.bldArea, 10) || 0,
          founded_year: parseInt(form.bldFounded, 10) || 0,
          harvesting_years: parseInt(form.bldHarvest, 10) || 0,
        },
        certification: {
          authority_zh: form.certAuthorityZh.trim(),
          authority_full: form.certAuthorityFull.trim(),
          reviewer_zh: form.certReviewer.trim(),
          date: form.certDate.trim(),
        },
        images: { hero: form.hero.trim(), gallery: gallery.map((g) => g.url.trim()) },
        captions_zh: gallery.map((g) => g.caption.trim()),
        narrative_zh: form.narrative.map((p) => p.trim()).filter(Boolean),
        source_url: form.source_url.trim(),
      });
      if (r.status === "ok") {
        showToast(r.created ? "已新增燕屋" : "已更新", "success");
        cancel();
        await refresh();
      } else if (r.status === "invalid_batch_range") {
        showToast(r.hint || "批號範圍有誤", "error");
      } else {
        showToast(`儲存未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    } finally {
      setSubmitting(false);
    }
  }

  async function remove(d: TraceabilityDoc) {
    if (!confirm(`確定刪除溯源燕屋「${d.doc_id}」?`)) return;
    try {
      const r = await adminDeleteTraceability(d.doc_id);
      if (r.status === "ok") {
        showToast("已刪除", "success");
        await refresh();
      } else {
        showToast(`刪除未成功:${r.status}`, "error");
      }
    } catch {
      showToast("連線異常", "error");
    }
  }

  return (
    <div className="min-h-screen bg-cream">
      <AdminHeader active="traceability" />
      <div className="p-6 max-w-3xl">
        {!editing ? (
          <>
            <div className="flex items-center justify-between mb-5">
              <h1 className="text-xl font-bold text-ink">溯源管理</h1>
              <button onClick={startNew} className="btn-primary">＋ 新增燕屋</button>
            </div>
            <div className="card">
              {loading ? (
                <div className="py-10 text-center text-muted">載入中…</div>
              ) : rows.length === 0 ? (
                <div className="py-10 text-center text-muted">尚無溯源資料,點「新增燕屋」建立第一筆。</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-muted text-xs border-b border-line">
                      <th className="py-2">燕屋 ID</th>
                      <th className="py-2">批號範圍</th>
                      <th className="py-2">產地</th>
                      <th className="py-2 text-right">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((d) => (
                      <tr key={d.doc_id} className="border-b border-line/50">
                        <td className="py-3 font-mono text-xs">{d.house_id || d.doc_id}</td>
                        <td className="py-3 font-mono text-xs">
                          {d.batch_range?.from}~{d.batch_range?.to}
                        </td>
                        <td className="py-3 text-muted text-xs">
                          {d.location?.country_zh} {d.location?.state_zh}
                        </td>
                        <td className="py-3 text-right space-x-3 whitespace-nowrap">
                          <button onClick={() => startEdit(d)} className="text-primary text-xs underline">
                            編輯
                          </button>
                          <button onClick={() => remove(d)} className="text-coral text-xs underline">
                            刪除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </>
        ) : (
          <>
            <div className="flex items-center gap-3 mb-5">
              <button onClick={cancel} className="text-primary text-xl">‹</button>
              <h1 className="text-xl font-bold text-ink">{form.doc_id ? `編輯:${form.doc_id}` : "新增燕屋"}</h1>
            </div>

            <Section title="基本資料">
              <Field label="批號範圍 起 *">
                <input className="input" type="number" value={form.batchFrom} onChange={(e) => set("batchFrom", e.target.value)} placeholder="150001" />
              </Field>
              <Field label="批號範圍 迄 *">
                <input className="input" type="number" value={form.batchTo} onChange={(e) => set("batchTo", e.target.value)} placeholder="154000" />
              </Field>
              <Field label="燕屋編號">
                <input className="input" value={form.house_id} onChange={(e) => set("house_id", e.target.value)} placeholder="730134" />
              </Field>
              <Field label="認證日期">
                <input className="input" value={form.certDate} onChange={(e) => set("certDate", e.target.value)} placeholder="2021/3/5" />
              </Field>
              <Field label="標題(中)" full>
                <input className="input" value={form.headline_zh} onChange={(e) => set("headline_zh", e.target.value)} placeholder="從大馬原生態燕屋,到您的餐桌" />
              </Field>
              <Field label="標題(英)" full>
                <input className="input" value={form.headline_en} onChange={(e) => set("headline_en", e.target.value)} placeholder="Provenance" />
              </Field>
            </Section>

            <Section title="產地 + GPS">
              <Field label="國家">
                <input className="input" value={form.country_zh} onChange={(e) => set("country_zh", e.target.value)} placeholder="馬來西亞" />
              </Field>
              <Field label="州">
                <input className="input" value={form.state_zh} onChange={(e) => set("state_zh", e.target.value)} placeholder="檳城" />
              </Field>
              <Field label="縣(中)">
                <input className="input" value={form.district_zh} onChange={(e) => set("district_zh", e.target.value)} placeholder="威南縣" />
              </Field>
              <Field label="縣(英)">
                <input className="input" value={form.district} onChange={(e) => set("district", e.target.value)} placeholder="Seberang Perai" />
              </Field>
              <Field label="GPS 緯度">
                <input className="input" value={form.gpsLat} onChange={(e) => set("gpsLat", e.target.value)} placeholder="5.141385" />
              </Field>
              <Field label="GPS 經度">
                <input className="input" value={form.gpsLng} onChange={(e) => set("gpsLng", e.target.value)} placeholder="100.495465" />
              </Field>
            </Section>

            <Section title="生態建築">
              <Field label="建築描述" full>
                <textarea className="input resize-none" rows={2} value={form.bldDesc} onChange={(e) => set("bldDesc", e.target.value)} placeholder="多層式專業燕屋,模擬天然洞穴環境…" />
              </Field>
              <Field label="層數">
                <input className="input" type="number" value={form.bldFloors} onChange={(e) => set("bldFloors", e.target.value)} />
              </Field>
              <Field label="占地(平方呎)">
                <input className="input" type="number" value={form.bldArea} onChange={(e) => set("bldArea", e.target.value)} />
              </Field>
              <Field label="創始年份">
                <input className="input" type="number" value={form.bldFounded} onChange={(e) => set("bldFounded", e.target.value)} />
              </Field>
              <Field label="採集年資">
                <input className="input" type="number" value={form.bldHarvest} onChange={(e) => set("bldHarvest", e.target.value)} />
              </Field>
            </Section>

            <Section title="官方認證">
              <Field label="認證機構(簡)">
                <input className="input" value={form.certAuthorityZh} onChange={(e) => set("certAuthorityZh", e.target.value)} placeholder="DVS" />
              </Field>
              <Field label="認證機構(全名)">
                <input className="input" value={form.certAuthorityFull} onChange={(e) => set("certAuthorityFull", e.target.value)} placeholder="馬來西亞獸醫服務局" />
              </Field>
              <Field label="審核單位" full>
                <input className="input" value={form.certReviewer} onChange={(e) => set("certReviewer", e.target.value)} />
              </Field>
            </Section>

            {/* 圖片 — 直接選檔上傳到我們圖庫,不用貼網址 */}
            <div className="card space-y-3 mb-4">
              <div className="font-bold text-ink text-sm">圖片</div>

              {/* 主圖 */}
              <div>
                <label className="label">主圖</label>
                <div className="flex items-center gap-3">
                  {form.hero ? (
                    <img src={form.hero} alt="" className="w-28 h-16 object-cover rounded border border-line shrink-0" />
                  ) : (
                    <div className="w-28 h-16 bg-cream rounded border border-line flex items-center justify-center text-muted text-xs shrink-0">無圖</div>
                  )}
                  <div className="flex flex-col gap-2">
                    <label className="btn-secondary text-sm px-4 py-1.5 cursor-pointer text-center">
                      {uploadingKey === "hero" ? "上傳中…" : form.hero ? "更換主圖" : "上傳主圖"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!!uploadingKey}
                        onChange={(e) => { pickHero(e.target.files?.[0]); e.target.value = ""; }}
                      />
                    </label>
                    {form.hero && (
                      <button onClick={() => set("hero", "")} className="text-coral text-xs underline">移除主圖</button>
                    )}
                  </div>
                </div>
              </div>

              {/* 工藝圖庫 */}
              <div className="text-sm font-medium text-muted pt-2">工藝圖庫(圖 + 圖說)</div>
              {form.gallery.map((g, i) => (
                <div key={i} className="border border-line rounded-lg p-3 space-y-2 bg-cream/40">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted">第 {i + 1} 張</span>
                    <button onClick={() => removeGallery(i)} className="text-coral text-xs underline">刪除</button>
                  </div>
                  <div className="flex items-center gap-3">
                    {g.url ? (
                      <img src={g.url} alt="" className="w-20 h-20 object-cover rounded border border-line shrink-0" />
                    ) : (
                      <div className="w-20 h-20 bg-cream rounded border border-line flex items-center justify-center text-muted text-xs shrink-0">無圖</div>
                    )}
                    <label className="btn-secondary text-sm px-4 py-1.5 cursor-pointer">
                      {uploadingKey === `g${i}` ? "上傳中…" : g.url ? "更換圖片" : "上傳圖片"}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        disabled={!!uploadingKey}
                        onChange={(e) => { replaceGallery(i, e.target.files?.[0]); e.target.value = ""; }}
                      />
                    </label>
                  </div>
                  <input className="input" value={g.caption} onChange={(e) => updateGallery(i, "caption", e.target.value)} placeholder="圖說" />
                </div>
              ))}
              <label className="btn-secondary text-sm w-full block text-center cursor-pointer">
                {uploadingKey === "new" ? "上傳中…" : "＋ 新增圖片"}
                <input
                  type="file"
                  accept="image/*"
                  className="hidden"
                  disabled={!!uploadingKey}
                  onChange={(e) => { addGalleryImage(e.target.files?.[0]); e.target.value = ""; }}
                />
              </label>
            </div>

            {/* 敘述 */}
            <div className="card space-y-3 mb-4">
              <div className="font-bold text-ink text-sm">品牌敘述(多段)</div>
              {form.narrative.map((p, i) => (
                <div key={i} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-muted">第 {i + 1} 段</span>
                    <button onClick={() => removeNarrative(i)} className="text-coral text-xs underline">刪除</button>
                  </div>
                  <textarea className="input resize-none" rows={2} value={p} onChange={(e) => updateNarrative(i, e.target.value)} />
                </div>
              ))}
              <button onClick={addNarrative} className="btn-secondary text-sm w-full">＋ 新增一段</button>
            </div>

            <div className="flex gap-3">
              <button onClick={cancel} className="btn-secondary flex-1" disabled={submitting}>取消</button>
              <button onClick={save} className="btn-primary flex-1" disabled={submitting}>
                {submitting ? "儲存中…" : form.doc_id ? "儲存變更" : "新增"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="card mb-4">
      <div className="font-bold text-ink text-sm mb-3">{title}</div>
      <div className="grid grid-cols-2 gap-3">{children}</div>
    </div>
  );
}

function Field({ label, children, full }: { label: string; children: ReactNode; full?: boolean }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
